import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye, FileText, Loader2, Lock, Search, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, API_URL, errMsg } from "@/lib/api";
import { fmtDate, fmtSize } from "@/lib/format";
import { ConfirmDialog, EmptyState, PageHeader, PaginationBar } from "@/components/common";

const CATEGORIES = ["Contracts", "Court Filings", "Evidence", "Correspondence", "Identification", "Financial", "General"];
const inputCls = "bg-white border border-border focus:border-gold px-3 py-2.5 outline-none transition text-sm rounded-none";

export default function Documents() {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploadClient, setUploadClient] = useState("");
  const [uploadCategory, setUploadCategory] = useState("General");
  const [deleting, setDeleting] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => {
    api.get("/clients?limit=100").then(({ data }) => {
      setClients(data.items);
      if (data.items.length && !uploadClient) setUploadClient(data.items[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ page, limit: 12 });
    if (debounced) q.set("search", debounced);
    if (clientFilter) q.set("client_id", clientFilter);
    if (category) q.set("category", category);
    api.get(`/documents?${q}`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [page, debounced, clientFilter, category]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = (files) => {
    if (!uploadClient) {
      toast.error("Select a client before uploading.");
      return;
    }
    [...files].forEach((file) => {
      const uid = `${file.name}-${Date.now()}-${Math.random()}`;
      setUploads((u) => [...u, { uid, name: file.name, progress: 0, status: "uploading" }]);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("client_id", uploadClient);
      fd.append("category", uploadCategory);
      api.post("/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / (e.total || 1));
          setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, progress: pct } : x)));
        },
      }).then(() => {
        setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, progress: 100, status: "done" } : x)));
        toast.success(`"${file.name}" uploaded securely`);
        setTimeout(() => setUploads((u) => u.filter((x) => x.uid !== uid)), 2500);
        load();
      }).catch((e) => {
        setUploads((u) => u.map((x) => (x.uid === uid ? { ...x, status: "error" } : x)));
        toast.error(errMsg(e));
      });
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/documents/${deleting.id}`);
      toast.success("Document deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const canPreview = (d) => d.content_type?.startsWith("image/") || d.content_type === "application/pdf" || d.content_type?.startsWith("text/");

  return (
    <div data-testid="documents-page">
      <PageHeader title="Documents" description="Client files, encrypted at rest and visible only to you." testId="documents-header" />

      <div className="bg-gold-100 border-l-4 border-gold px-5 py-4 mb-6 flex gap-3 items-start" data-testid="confidentiality-banner">
        <Lock className="h-4 w-4 text-gold-700 mt-0.5 shrink-0" />
        <p className="text-sm text-navy-700 leading-relaxed">
          <span className="font-semibold">Attorney–client privilege.</span> All documents are stored in an encrypted private vault, tied to individual client matters, and never shared or indexed publicly.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInput.current?.click()}
            data-testid="upload-dropzone"
            className={`cursor-pointer border-2 border-dashed p-10 text-center transition-all duration-300 bg-white ${dragOver ? "border-gold bg-gold-100/40" : "border-border hover:border-gold/60"}`}>
            <UploadCloud className={`h-10 w-10 mx-auto mb-4 transition-colors ${dragOver ? "text-gold-700" : "text-muted-foreground"}`} />
            <p className="font-serif text-xl font-semibold text-navy">
              {dragOver ? "Drop files to upload" : "Drag & drop documents here"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">or click to browse — PDF, Word, images up to 15 MB</p>
            <input ref={fileInput} type="file" multiple className="hidden" data-testid="upload-file-input"
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
          </div>

          {uploads.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="upload-progress-list">
              {uploads.map((u) => (
                <div key={u.uid} className="bg-white border border-border px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-navy truncate mr-4">{u.name}</span>
                    <span className={`text-xs font-semibold uppercase tracking-wider ${u.status === "error" ? "text-red-600" : u.status === "done" ? "text-emerald-600" : "text-gold-700"}`}>
                      {u.status === "error" ? "Failed" : u.status === "done" ? "Uploaded" : `${u.progress}%`}
                    </span>
                  </div>
                  <div className="h-1 bg-muted mt-2">
                    <div className={`h-full transition-all duration-300 ${u.status === "error" ? "bg-red-500" : "bg-gold"}`}
                      style={{ width: `${u.status === "error" ? 100 : u.progress}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-border p-6 space-y-5 h-fit" data-testid="upload-settings">
          <h3 className="font-serif text-lg font-semibold text-navy">Upload destination</h3>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">Client</label>
            <select className={`${inputCls} w-full mt-1.5`} value={uploadClient} onChange={(e) => setUploadClient(e.target.value)} data-testid="upload-client-select">
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">Category</label>
            <select className={`${inputCls} w-full mt-1.5`} value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} data-testid="upload-category-select">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input className={`${inputCls} w-full pl-10`} placeholder="Search documents…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="document-search-input" />
        </div>
        <select className={`${inputCls} sm:w-52`} value={clientFilter} onChange={(e) => { setClientFilter(e.target.value); setPage(1); }} data-testid="document-client-filter">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className={`${inputCls} sm:w-48`} value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} data-testid="document-category-filter">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {!data ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-white border border-border animate-pulse" />)}
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState icon={FileText} title="No documents found"
          description={debounced || clientFilter || category ? "Try different filters." : "Upload your first document using the dropzone above."}
          testId="documents-empty" />
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
            {data.items.map((d) => (
              <div key={d.id} className="bg-white border border-border p-5 hover:border-gold hover:shadow-card transition-all duration-300 group" data-testid={`document-card-${d.id}`}>
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 bg-gold-100 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-gold-700" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-navy text-sm truncate" title={d.name}>{d.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{d.client_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 text-[11px] text-muted-foreground">
                  <span className="px-2 py-0.5 bg-muted uppercase tracking-wider font-semibold">{d.category}</span>
                  <span>{fmtSize(d.size)}</span>
                  <span>·</span>
                  <span>{fmtDate(d.uploaded_at)}</span>
                </div>
                <div className="flex gap-1 mt-4 pt-3 border-t border-border opacity-0 group-hover:opacity-100 transition-opacity">
                  {canPreview(d) && (
                    <a href={`${API_URL}/documents/${d.id}/download?inline=true`} target="_blank" rel="noreferrer"
                      data-testid={`preview-doc-${d.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold uppercase tracking-wider text-navy hover:bg-muted transition-colors">
                      <Eye className="h-3.5 w-3.5" /> Preview
                    </a>
                  )}
                  <a href={`${API_URL}/documents/${d.id}/download`} data-testid={`download-doc-${d.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold uppercase tracking-wider text-navy hover:bg-muted transition-colors">
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                  <button onClick={() => setDeleting(d)} data-testid={`delete-doc-${d.id}`}
                    className="px-3 py-2 text-red-500 hover:bg-red-50 transition-colors" aria-label="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <PaginationBar page={data.page} pages={data.pages} total={data.total} onChange={setPage} testId="documents-pagination" />
        </>
      )}

      <ConfirmDialog open={!!deleting} onOpenChange={() => setDeleting(null)}
        title="Delete this document?"
        description={`"${deleting?.name}" will be permanently removed from the vault. This cannot be undone.`}
        confirmLabel="Delete Document" onConfirm={doDelete} />
    </div>
  );
}
